import { API } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { FileDown, LogIn, LayoutDashboard, FileText, PlusCircle, Truck, ListChecks, ClipboardCheck, Languages, BookOpen, Users } from "lucide-react";

const TUTORIAL_PDF_URL = "https://drive.google.com/file/d/1UThk2au3w8_aD_J-6l9MmHDBy58A1ie9/view";

const SECTIONS = [
  {
    icon: LogIn,
    title: "Se connecter à l'application",
    bullets: [
      "Rendez-vous sur l'URL de l'application fournie par votre administrateur HSE.",
      "Cliquez sur « Se connecter avec Google » et utilisez votre compte Google professionnel.",
      "Votre rôle (Administrateur, Rédacteur…) est attribué par un administrateur dans l'onglet Administration.",
    ],
    image: "login.jpeg",
  },
  {
    icon: LayoutDashboard,
    title: "Le tableau de bord",
    bullets: [
      "Vue d'ensemble : total de protocoles, validés, à valider, obsolètes, échéances proches.",
      "Traductions à actualiser et score de conformité global.",
      "Astuce : chaque carte est cliquable et amène directement à la liste filtrée correspondante.",
    ],
    image: "dashboard.jpeg",
  },
  {
    icon: FileText,
    title: "La liste des protocoles",
    bullets: [
      "Filtrez par site, type d'opération ou statut.",
      "Recherchez un protocole par titre ou identifiant, ou dupliquez un protocole existant.",
    ],
    image: "protocoles_list.jpeg",
  },
  {
    icon: PlusCircle,
    title: "Créer un nouveau protocole",
    bullets: [
      "Renseignez le titre, le site, le type d'opération et le cadre (entreprise, responsable, tonnage, produit…).",
      "Le référentiel HSE pré-remplit automatiquement les règles, risques et EPI pertinents.",
    ],
    image: "create_protocol.jpeg",
  },
  {
    icon: Truck,
    title: "Choisir le type de véhicule",
    bullets: [
      "Chaque véhicule est illustré par un pictogramme réaliste, repris sur la fiche et le PDF final.",
    ],
    image: "vehicule_select.jpeg",
  },
  {
    icon: FileText,
    title: "La fiche protocole",
    bullets: [
      "Onglet Informations : statut, version, actions rapides (Modifier, Soumettre, Archiver, Export PDF, Traduire en anglais).",
    ],
    image: "detail_main.jpeg",
  },
  {
    icon: ListChecks,
    title: "Sections : risques & EPI illustrés",
    bullets: [
      "Chaque risque est représenté par un pictogramme ISO 7010, chaque EPI par une icône claire.",
      "Objectif : un chauffeur de n'importe quelle nationalité comprend les consignes sans devoir tout lire.",
    ],
    image: "detail_sections.jpeg",
  },
  {
    icon: ClipboardCheck,
    title: "Cadre du protocole & Conformité",
    bullets: [
      "Le cadre reprend la trame officielle : entreprises, responsables, coordonnées d'urgence, ADR.",
      "Le score de conformité est calculé automatiquement selon le référentiel HSE.",
    ],
    image: "detail_cadre.jpeg",
  },
  {
    icon: ClipboardCheck,
    title: "Workflow de validation",
    bullets: [
      "Un rédacteur soumet son protocole « À valider ». Un administrateur le Valide ou le Refuse.",
    ],
    image: "a_valider.jpeg",
  },
  {
    icon: Languages,
    title: "Traduction assistée par IA",
    bullets: [
      "Le français reste toujours la version maîtresse. L'IA génère une traduction anglaise fidèle.",
      "Chaque traduction obsolète (protocole modifié depuis) est signalée automatiquement.",
    ],
    image: "traductions.jpeg",
  },
  {
    icon: BookOpen,
    title: "Le référentiel HSE : règles, risques, EPI, véhicules",
    bullets: [
      "40 règles, 21 risques, 10 EPI et 6 types de véhicules, chacun avec son propre pictogramme.",
      "Onglet Produits : recherchez un produit et consultez ses dangers, EPI de prévention et documents FDS/FHD.",
    ],
    image: "ref_regles.jpeg",
  },
  {
    icon: Users,
    title: "Administration",
    bullets: [
      "Gérez les utilisateurs (rôles) et les sites Lafarge/Holcim couverts par l'application.",
    ],
    image: "administration.jpeg",
  },
];

export default function Aide() {
  const { t } = useI18n();
  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="aide-view">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{t("nav.help")}</h1>
          <p className="text-sm text-slate-500 mt-1">Guide de formation illustré — pour les équipes HSE et les chauffeurs transporteurs.</p>
        </div>
        <a
          href={TUTORIAL_PDF_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="download-tutorial-pdf-btn"
          className="inline-flex items-center gap-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2.5 transition-colors shrink-0"
        >
          <FileDown className="w-4 h-4" />
          Télécharger le PDF imprimable
        </a>
      </div>

      <div className="space-y-4">
        {SECTIONS.map((section, idx) => (
          <div key={idx} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 flex flex-col md:flex-row gap-5" data-testid={`aide-section-${idx}`}>
            <div className="flex-1 space-y-2 md:max-w-sm">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                  <section.icon className="w-4 h-4" />
                </span>
                <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">
                  {idx + 1}. {section.title}
                </h2>
              </div>
              <ul className="space-y-1.5 pl-1">
                {section.bullets.map((b, i) => (
                  <li key={i} className="text-sm text-slate-600 dark:text-slate-400 flex gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-1 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-slate-50 dark:bg-slate-950">
              <img src={`${API}/static/tutorial/${section.image}`} alt={section.title} className="w-full h-auto object-contain" loading="lazy" data-testid={`aide-section-image-${idx}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
